import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import Modal from 'core/elements/modal'
import Button from 'core/elements/button/Button'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import NumberedStepLabel from 'core/components/validatedForm/NumberedStepLabel'
import CodeBlock from 'core/components/CodeBlock'
import CopyToClipboard from 'core/components/CopyToClipboard'
import ExternalLink from 'core/components/ExternalLink'
import { troubleshootingHostsDoc } from 'app/docLinks'
import clsx from 'clsx'

const maxDialogWidth = 1000

const errorStatuses = ['not-applied', 'auth-error', 'deauth-error']
const successStatuses = ['applied']

const logFileMap = {
  'pf9-neutron-base': '/var/log/pf9/ostackhost.log',
  'pf9-neutron-ovn-controller': '/var/log/pf9/ovn/ovn-controller.log',
  'pf9-neutron-ovn-metadata-agent': '/var/log/pf9/pf9-neutron-ovn-metadata-agent.log',
  'pf9-ostackhost-neutron': '/var/log/pf9/ostackhost.log',
  'pf9-cindervolume-base': '/var/log/pf9/cindervolume-base.log',
  'pf9-cindervolume-config': '/var/log/pf9/cindervolume-base.log',
}

interface Role {
  role: string
  status: string
  display: string
}

interface Props {
  host: any
  role: Role
  subRoles: string[]
  onClose: () => void
}

const UberRoleErrorDialog = ({ host, role, subRoles, onClose }: Props) => {
  const classes = useStyles()

  const failedApply = role?.status === 'Applying role failed'

  const subrolesWithStatus = useMemo(() => {
    return subRoles.map((subRole) => ({
      role: subRole,
      status: host?.v1RoleDetails?.[subRole],
    }))
  }, [host, subRoles])

  const subrolesByCategory = useMemo(() => {
    return subrolesWithStatus.reduce(
      (accum, subRole) => {
        if (successStatuses.includes(subRole.status)) {
          return {
            ...accum,
            success: [...accum.success, subRole],
          }
        } else if (errorStatuses.includes(subRole.status)) {
          return {
            ...accum,
            error: [...accum.error, subRole],
          }
        } else {
          return {
            ...accum,
            pending: [...accum.pending, subRole],
          }
        }
      },
      {
        success: [],
        pending: [],
        error: [],
      },
    )
  }, [subrolesWithStatus])

  return (
    <Modal
      title={`Role Application Failed: ${role.display} (${host.name})`}
      onClose={onClose}
      maxWidth={maxDialogWidth}
      panel="dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
      open
    >
      <Text variant="body2">
        The {role.display} role could not be {failedApply ? 'applied to' : 'removed from'} this
        host.{' '}
        <b>
          {subrolesByCategory?.error?.length} of {subRoles?.length} components are in a failed
          state.
        </b>
      </Text>
      {!!subrolesByCategory?.error?.length && (
        <>
          <Text variant="subtitle2">Failed Components</Text>
          <div className={clsx(classes.box, classes.error)}>
            {subrolesByCategory.error.map((subrole) => (
              <div key={subrole.role} className={classes.service}>
                <FontAwesomeIcon className={classes.errorIcon} solid>
                  circle-xmark
                </FontAwesomeIcon>
                <span>{subrole.role}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {!!subrolesByCategory?.success?.length && (
        <>
          <Text variant="subtitle2">Successfully Applied Components</Text>
          <div className={clsx(classes.box, classes.success)}>
            {subrolesByCategory.success.map((subrole) => (
              <div key={subrole.role} className={classes.service}>
                <FontAwesomeIcon className={classes.successIcon} solid>
                  circle-check
                </FontAwesomeIcon>
                <span>{subrole.role}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {!!subrolesByCategory?.pending?.length && (
        <>
          <Text variant="subtitle2">Pending Components</Text>
          <div className={clsx(classes.box, classes.pending)}>
            {subrolesByCategory.pending.map((subrole) => (
              <div key={subrole.role} className={classes.service}>
                <FontAwesomeIcon spin>circle-notch</FontAwesomeIcon>
                <span>{subrole.role}</span>
              </div>
            ))}
          </div>
        </>
      )}
      <Text variant="subtitle2">Troubleshooting Steps</Text>
      <div className={clsx(classes.infoBox, classes.info)}>
        <NumberedStepLabel
          step={1}
          title="Check the hostagent log for detailed error messages"
          className={classes.troubleshootingStep}
        />
        <div className={classes.codeBlocks}>
          <CopyToClipboard copyText={`tail -n 100 /var/log/pf9/hostagent.log`}>
            <CodeBlock>
              <Text variant="body2">{`tail -n 100 /var/log/pf9/hostagent.log`}</Text>
            </CodeBlock>
          </CopyToClipboard>
        </div>
        <NumberedStepLabel
          step={2}
          title="Check the documentation for common host issues"
          className={classes.troubleshootingStep}
        />
        <div className={classes.tabbed}>
          <ExternalLink url={troubleshootingHostsDoc}>Hypervisor or Host Issues</ExternalLink>
        </div>
        <NumberedStepLabel
          step={3}
          title="Check component logs on this host for each failed component"
          className={classes.troubleshootingStep}
        />
        <div className={clsx(classes.tabbed, classes.logs)}>
          {subrolesByCategory?.error?.map((subrole) => (
            <React.Fragment key={subrole.role}>
              <Text variant="caption1">{subrole.role}</Text>
              <Text variant="body2">-</Text>
              <Text variant="body2">{logFileMap[subrole.role]}</Text>
            </React.Fragment>
          ))}
        </div>
        <NumberedStepLabel
          step={4}
          title="After resolving issues, try re-applying the role"
          className={classes.troubleshootingStep}
        />
      </div>
    </Modal>
  )
}

export default UberRoleErrorDialog

const useStyles = makeStyles<Theme>((theme) => ({
  box: {
    display: 'grid',
    borderRadius: 4,
    padding: '16px 24px',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  error: {
    border: `1px solid ${theme.palette.red[500]}`,
    background: theme.palette.red[100],
  },
  errorIcon: {
    color: theme.palette.red[500],
  },
  success: {
    border: `1px solid ${theme.palette.green[500]}`,
    background: theme.palette.green[100],
  },
  successIcon: {
    color: theme.palette.green[500],
  },
  pending: {
    border: `1px solid ${theme.palette.yellow[500]}`,
    background: theme.palette.yellow[100],
  },
  pendingIcon: {
    color: theme.palette.yellow[500],
  },
  infoBox: {
    display: 'grid',
    borderRadius: 4,
    padding: '24px',
    gap: 8,
  },
  info: {
    border: `1px solid ${theme.palette.blue[500]}`,
    background: theme.palette.blue[100],
  },
  service: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  troubleshootingStep: {
    '& .title': {
      ...theme.typography.body2,
    },
  },
  tabbed: {
    paddingLeft: 42,
  },
  codeBlocks: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(5),
  },
  logs: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content max-content',
    gap: '8px 16px',
  },
}))
